import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import "./features.css";

import resumeShot from "../../../assets/Resumes and cover letters.webp";
import jobBoardShot from "../../../assets/jobboard.webp";
import networkShot from "../../../assets/networking-job-seeker.webp";
import interviewShot from "../../../assets/job-seeker-interview.webp";
import exploreShot from "../../../assets/career-exploration.webp";
import tasksShot from "../../../assets/tasks.webp";
import planShot from "../../../assets/to-do.webp";
import studentPhoto from "../../../assets/job-seeker pic.webp";

/* The frame opens on the same schedule the hero ends on, so the section
   reads as a continuation of it rather than a new picture. */
const introShot = tasksShot;

/* The eight surfaces a student actually opens in RAIN, in the order the
   search tends to move through them: build the materials, find the roles,
   work the process, then keep it all on a schedule.

   `tone` is only used by the stacked layout below, where each row's label
   takes its own accent. */
const features = [
  {
    key: "explore",
    tone: "blue",
    label: "Career exploration",
    summary: "Figure out what you’re aiming for before you apply.",
    body:
      "Short assessments map what you enjoy to real roles, and guides show what those paths look like day to day. That is enough to narrow the list now, instead of finding out the hard way a semester of applications later.",
    image: exploreShot,
    alt: "RAIN interest and personality assessments alongside the interest profile they produce, with top career matches",
  },
  {
    key: "jobs",
    tone: "violet",
    label: "Job board and tracker",
    summary: "Find the roles, then keep every one of them straight.",
    body:
      "Internships and entry-level openings pulled from across the web and filtered by class year, industry, and location. Anything worth a shot goes to your tracker in a click, and the dates, stages, and follow-ups stay in order from there.",
    image: jobBoardShot,
    alt: "RAIN job board listing internships and entry-level roles",
  },
  {
    key: "materials",
    tone: "teal",
    label: "Resumes and cover letters",
    summary: "Write them here, then have them pulled apart line by line.",
    body:
      "Draft your bullets with an assistant that asks what you actually did, and let RAIN draft each letter from your resume and the posting it’s aimed at. Both come back reviewed for inconsistent dates, vague deliverables, and the generic lines a hiring manager has read a hundred times, each with a one-click fix.",
    image: resumeShot,
    alt: "RAIN resume and cover letter drafting alongside the review that flags fixes",
  },
  {
    key: "networking",
    tone: "coral",
    label: "Networking",
    summary: "The alumni worth talking to, and what to say.",
    body:
      "RAIN surfaces people from your school at the companies you’re targeting, finds the email address, drafts the outreach, and keeps every coffee chat and follow-up on a schedule.",
    image: networkShot,
    alt: "An applied role in RAIN leading to a list of recommended connections at that company, each with an email address",
  },
  {
    key: "interviews",
    tone: "blue",
    label: "Interview prep",
    summary: "The questions you’ll get asked, and honest feedback.",
    body:
      "Work through the question bank for your role and the companies on your list, then record practice answers and get notes on your examples, your eye contact, and the filler words you never notice.",
    image: interviewShot,
    alt: "A RAIN practice interview question with a recorded answer and AI feedback on examples, eye contact and filler words",
  },
  {
    key: "plan",
    tone: "violet",
    label: "Your weekly plan",
    summary: "A short list of what matters this week.",
    body:
      "Everything above lands here: a handful of tasks that move the search forward, in the order they should happen, and they change as your applications, deadlines, and conversations do.",
    image: planShot,
    alt: "RAIN weekly task list for a student job search",
  },
];

/* The two pieces that flank the schedule. Same markup either way — what
   changes is where the stylesheet puts them. */
const FlankingPhoto = () => (
  <figure className="s-flow-photo">
    <img width={1122} height={631} decoding="async"
      src={studentPhoto}
      alt="A student working through applications on a laptop in a campus library"
    />
  </figure>
);

const FlankingCard = () => (
  <aside className="s-flow-card">
    <p className="s-flow-card-title">
      <Sparkles size={14} strokeWidth={2} />
      Drafted your outreach
    </p>
    <p className="s-flow-card-body">
      Jessica graduated from your program and now works on the team you applied
      to. Here is an opener you can send.
    </p>
    <span className="s-flow-card-chip">Reach out to Jessica Anderson</span>
  </aside>
);

/* ==================================================================
   The scroll track — the wide-screen layout.
   The section is a tall track with a pinned stage inside it. The first
   stretch holds the product on its own, centred and wide; then the stage
   splits — the shot slides right and narrows while the list opens out
   from the left — and from there scrolling walks the list.
   ================================================================== */
const INTRO_VH = 80;
const STEP_VH = 40;
const TOTAL_VH = INTRO_VH + STEP_VH * features.length;

/* Three beats, in fractions of the track:
   0 → SWAP_AT   the schedule holds while the flanking pieces scroll away
   SWAP_AT       the frame changes to the first feature, still centred
   SPLIT_AT      it docks right and the list opens out from the left */
const SWAP_AT = 0.13;
const SPLIT_AT = 0.26;

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const FeaturesScroll = () => {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(features[0].key);
  const [swapped, setSwapped] = useState(false);
  const [split, setSplit] = useState(false);

  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      const el = sectionRef.current;
      if (!el) return;
      const travel = el.offsetHeight - window.innerHeight;
      if (travel <= 0) return;

      const progress = clamp(-el.getBoundingClientRect().top / travel, 0, 1);
      setSwapped(progress >= SWAP_AT);
      setSplit(progress >= SPLIT_AT);

      const segment = (1 - SPLIT_AT) / features.length;
      const index = clamp(
        Math.floor((progress - SPLIT_AT) / segment),
        0,
        features.length - 1
      );
      setActive(features[index].key);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    read();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  /* Clicking a label scrolls to the middle of that feature's stretch, so the
     list stays honest about what the scroll position is showing. */
  const goTo = useCallback((index) => {
    const el = sectionRef.current;
    if (!el) return;
    const travel = el.offsetHeight - window.innerHeight;
    if (travel <= 0) {
      setActive(features[index].key);
      return;
    }
    const segment = (1 - SPLIT_AT) / features.length;
    const progress = SPLIT_AT + segment * (index + 0.5);
    window.scrollTo({ top: el.offsetTop + progress * travel, behavior: "smooth" });
  }, []);

  return (
    <section
      className="s-flow s-flow-scroll"
      id="features"
      ref={sectionRef}
      style={{ height: `${TOTAL_VH}vh` }}
    >
      {/* These two sit in the section, not in the pinned stage, so they
          scroll up and out of frame on their own while the schedule stays. */}
      <FlankingPhoto />
      <FlankingCard />

      <h2 className="sr-only">What RAIN does for your search</h2>

      <div className="s-flow-sticky">
        <div className={`s-flow-row${split ? " s-flow-split" : ""}`}>
          <div className="s-flow-copy">
            <div className="s-features-list">
              {features.map((feature, index) => {
                const isOpen = feature.key === active;
                return (
                  <div
                    key={feature.key}
                    className={`s-feature${isOpen ? " s-feature-open" : ""}`}
                  >
                    <button
                      type="button"
                      className="s-feature-label"
                      aria-expanded={isOpen}
                      aria-controls={`s-feature-body-${feature.key}`}
                      onClick={() => goTo(index)}
                    >
                      {feature.label}
                    </button>

                    {/* Always rendered, collapsed to 0fr when closed — a mounted
                        element is what lets the open/close actually animate. */}
                    <div
                      id={`s-feature-body-${feature.key}`}
                      className="s-feature-body"
                      aria-hidden={!isOpen}
                    >
                      <div className="s-feature-body-inner">
                        <p className="s-feature-summary">{feature.summary}</p>
                        <p className="s-feature-text">{feature.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="s-flow-visual">
            {/* Every shot is stacked and pre-loaded; only the active one is
                lit. Swapping opacity beats remounting an <img decoding="async">, which would
                flash an empty frame while the next file decodes. */}
            <div className="s-features-window">
              <img decoding="async"
                src={introShot}
                alt={swapped ? "" : "A RAIN generated schedule for the week ahead"}
                className={`s-window-shot${swapped ? "" : " s-window-shot-on"}`}
                aria-hidden={swapped}
              />

              {features.map((feature) => (
                <img decoding="async"
                  key={feature.key}
                  src={feature.image}
                  alt={swapped && feature.key === active ? feature.alt : ""}
                  className={`s-window-shot${
                    swapped && feature.key === active ? " s-window-shot-on" : ""
                  }`}
                  aria-hidden={!swapped || feature.key !== active}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ==================================================================
   The stacked rows — the narrow-screen layout.
   A pinned track needs a viewport to pin against and a scroll budget to
   spend, and a phone has neither: the stage there collapsed to one open
   row above a stack of labels that did nothing. So the same six surfaces
   are laid out plainly instead — copy, then the shot, all the way down —
   under the opening collage, which keeps both of its flanking pieces.
   ================================================================== */
const FeaturesRows = () => (
  <section className="s-flow s-flow-rows" id="features">
    <div className="home-container">
      {/* The section carries no title in the design — the rows tell their own
          story. It still needs one in the outline, or the row headings read as
          h3s hanging straight off the page h1. */}
      <h2 className="sr-only">What RAIN does for your search</h2>

      <div className="s-stage">
        <div className="s-stage-shot">
          <img decoding="async" src={introShot} alt="A RAIN generated schedule for the week ahead" />
        </div>
        <FlankingPhoto />
        <FlankingCard />
      </div>

      {features.map((feature, i) => (
        <article
          key={feature.key}
          /* Odd rows put the shot on the left, so the page zig-zags. */
          className={`s-row s-row-${feature.tone}${i % 2 ? " s-row-flip" : ""}`}
        >
          <div className="s-row-copy">
            <span className="s-row-eyebrow">{feature.label}</span>
            <h3 className="s-row-title">{feature.summary}</h3>
            <p className="s-row-body">{feature.body}</p>
          </div>

          <div className="s-row-visual">
            <img decoding="async" src={feature.image} alt={feature.alt} loading="lazy" />
          </div>
        </article>
      ))}
    </div>
  </section>
);

/* Which of the two renders. The scroll track is a wide-screen effect: it
   needs the room to dock a 620px frame beside a list, and it needs the
   scroll budget, so a narrow screen gets the stacked rows instead. Anyone
   who asked for less motion gets them at every width. */
const ROWS_QUERY = "(max-width: 1000px), (prefers-reduced-motion: reduce)";

const Features = () => {
  const [stacked, setStacked] = useState(
    () => typeof window !== "undefined" && window.matchMedia(ROWS_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(ROWS_QUERY);
    const onChange = () => setStacked(mq.matches);
    mq.addEventListener("change", onChange);
    onChange();
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return stacked ? <FeaturesRows /> : <FeaturesScroll />;
};

export default Features;
